import React, {useMemo} from "react";
import {useSearchParams} from "react-router-dom";
import {BillingAccountSummary, BillingSummary,} from "@/domain/BillingAccountSummary";
import {accountTypeNames} from "@/constatants";
import {useAccountStore} from "@/store/useAccountStore";
import {FullAccountTable, FullAccountTableAccountRow, FullAccountTableTotalRow} from "./main/FullAccountTable";
import {YmTermInput} from "@/component/input/YmTermInput";
import {MidAccountTable, MidAccountTableRow} from "@/page/main/MidAccountTable";
import {ExternalAccountTable, ExternalAccountTableRow} from "@/page/main/ExternalAccountTable";
import {FtGrid, FtGridCol} from "@/component/layout/FtGrid";
import {BillingAccountType, TransAccount} from "@/common/dto/TransAccount";
import {FtDate} from "@/common/util/FtDate";
import {useTransactionSummary} from "@/hook/useTransactions";
import {TransSum} from "@/common/dto/TransSum";
import {useTransLimitsSummary} from "@/hook/useTransLimits";
import {TransLimitSummary} from "@/common/dto/TransLimitSummary";

interface TypeSummary {
    type: string;
    typeName: string;
    accounts: BillingAccountSummary[];
    sum: BillingSummary;
}

interface MainPageData {
    liquidity: TypeSummary[];
    liquiditySum: BillingSummary;
    illquidSum: BillingSummary;
    income: TypeSummary;
    expense: TypeSummary;
    saving: TypeSummary;
    investment: TypeSummary;
    transLimitBalance: BillingSummary;
}

export function MainPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const accounts = useAccountStore(state => state.accounts);

    const ymFrom = searchParams.get("ymFrom") || new FtDate().ym();
    const ymTo = searchParams.get("ymTo") || new FtDate().ym();

    const dateFrom = new FtDate(ymFrom).ymd();
    const dateTo = new FtDate(ymTo).lastDayOfMonth().ymd();
    const prevDate = new FtDate(ymFrom).addDay(-1).ymd();

    const {data: carry} = useTransactionSummary(undefined, prevDate);
    const {data: periodSum} = useTransactionSummary(dateFrom, dateTo);
    const {data: transLimitCarry} = useTransLimitsSummary(undefined, undefined, prevDate);
    const {data: transLimitSum} = useTransLimitsSummary(undefined, dateFrom, dateTo);

    const data = useMemo(() => {
        if (!accounts || accounts.length === 0) return undefined;
        if (!carry || !periodSum) return undefined;
        if (!transLimitSum || !transLimitCarry) return undefined;
        return processData(carry, periodSum, transLimitCarry, transLimitSum, accounts);
    }, [carry, periodSum, transLimitSum, accounts]);

    const transactionsParams = useMemo(() => `ymdFrom=${dateFrom}&ymdTo=${dateTo}`, [ymFrom, ymTo]);

    if (!data) {
        return <div>Loading...</div>;
    }

    return <div>
        <YmTermInput ymFrom={ymFrom} ymTo={ymTo} mode='MONTH' onChange={(ymFrom, ymTo) => {
            setSearchParams({ymFrom: ymFrom, ymTo: ymTo});
        }}/>

        <div style={{height: 16}}/>

        <FtGrid>
            <FtGridCol>
                <h3>유동성</h3>
                <FullAccountTable>
                    {data?.liquidity.map((item) => (
                        <TypeSummaryRows data={item} key={item.type} transactionsParams={transactionsParams}/>
                    ))}
                    {data && (
                        <FullAccountTableTotalRow
                            name="합계" amounts={data.liquiditySum} className="total"/>
                    )}
                </FullAccountTable>

                <h3>요약</h3>
                <MidAccountTable>
                    <MidAccountTableRow name="유동성" amounts={data.liquiditySum}/>
                    <MidAccountTableRow name="저축" amounts={data.saving.sum}/>
                    <MidAccountTableRow name="투자" amounts={data.investment.sum}/>
                    <MidAccountTableRow name="저축 + 투자" amounts={data.saving.sum.add(data.investment.sum)}/>
                    <MidAccountTableRow
                        className="total"
                        name="전체"
                        amounts={data.liquiditySum.add(data.illquidSum)}
                    />
                    <MidAccountTableRow name="용돈잔고" amounts={data.transLimitBalance}/>
                    <MidAccountTableRow name="유동성 + 저축 - 용돈잔고" amounts={data.liquiditySum.add(data.saving.sum).subtract(data.transLimitBalance)}/>
                    <MidAccountTableRow className="total" name="전체 - 용돈잔고" amounts={data.liquiditySum.add(data.saving.sum).add(data.investment.sum).subtract(data.transLimitBalance)}/>
                </MidAccountTable>
            </FtGridCol>

            <FtGridCol>
                <FtGrid>
                    <FtGridCol>
                        <h3>수입</h3>
                        <ExternalAccountTable>
                            <ShortTypeSummaryRows
                                data={data.income}
                                transactionsParams={transactionsParams}
                            />
                        </ExternalAccountTable>
                    </FtGridCol>
                    <FtGridCol>
                        <h3>지출</h3>
                        <ExternalAccountTable>
                            <ShortTypeSummaryRows
                                data={data.expense}
                                transactionsParams={transactionsParams}
                            />
                        </ExternalAccountTable>
                    </FtGridCol>
                </FtGrid>

                <h3>저축</h3>
                <FullAccountTable>
                    <TypeSummaryRows data={data.saving} transactionsParams={transactionsParams}/>
                </FullAccountTable>
                
                <h3>투자</h3>
                <FullAccountTable>
                    <TypeSummaryRows data={data.investment} transactionsParams={transactionsParams}/>
                </FullAccountTable>
            </FtGridCol>
        </FtGrid>
    </div>;
}

function filterAccountType(
    summary: BillingAccountSummary[],
    type: BillingAccountType,
): TypeSummary {
    const result = summary
        .filter((item) => item.account.type === type)
        .filter((item) => item.amounts.isMeaningful());

    result.sort((a, b) => {
        return a.account.name.localeCompare(b.account.name);
    });

    const sum = result.map((item) => item.amounts).reduce((p, c) => p.add(c), BillingSummary.zero());

    return {
        type: type,
        typeName: accountTypeNames[type],
        accounts: result,
        sum: sum,
    };
}

function getPeriodSummary(
    carryMap: Record<number, TransSum>,
    inOutMap: Record<number, TransSum>,
    accounts: TransAccount[]
): BillingAccountSummary[] {
    const result: BillingAccountSummary[] = [];
    for (const account of accounts) {
        const carryInOut = carryMap[account.id];

        const carryAmount =
            +account.startAmount +
            (carryInOut ? carryInOut["inSum"] - carryInOut["outSum"] : 0);
        const inAmount = inOutMap[account.id] ? inOutMap[account.id].inSum : 0;
        const outAmount = inOutMap[account.id] ? inOutMap[account.id].outSum : 0;
        const amounts = new BillingSummary(carryAmount, inAmount, outAmount);

        result.push({
            account,
            amounts,
        });
    }
    return result;
}

function processData(
    carryMap: Record<number, TransSum>,
    inOutMap: Record<number, TransSum>,
    transLimitCarries: TransLimitSummary[],
    transLimitSums: TransLimitSummary[],
    accounts: TransAccount[]
): MainPageData {
    const summary = getPeriodSummary(carryMap, inOutMap, accounts);

    const liquidity = [
        filterAccountType(summary, "CASH"),
        filterAccountType(summary, "BANK"),
        filterAccountType(summary, "CARD"),
        filterAccountType(summary, "EXT"),
    ];

    const liquiditySum =
        liquidity.map((item) => item.sum).reduce((p, c) => p.add(c)) ||
        BillingSummary.zero();

    const income = filterAccountType(summary, "INC");
    const expense = filterAccountType(summary, "EXP");
    const saving = filterAccountType(summary, "SAVE");
    const investment = filterAccountType(summary, "INVESTMENT");

    let transLimitCarry = transLimitCarries.reduce((acc, item) => item.amount + acc, 0);
    let transLimitIn = transLimitSums.reduce((acc, item) => item.amount + acc, 0);
    let transLimitOut = 0;

    for (const account of accounts) {
        if (account.useLimit) {
            transLimitCarry += carryMap[account.id]?.outSum ?? 0;
            transLimitCarry -= carryMap[account.id]?.inSum ?? 0;
            transLimitOut -= inOutMap[account.id]?.outSum ?? 0;
            transLimitOut += inOutMap[account.id]?.inSum ?? 0;
        }
    }
    const transLimitBalance = new BillingSummary(transLimitCarry, transLimitIn, transLimitOut);

    return {
        liquidity,
        liquiditySum,
        illquidSum: saving.sum.add(investment.sum),
        income,
        expense,
        saving,
        investment,
        transLimitBalance,
    };
}

function TypeSummaryRows(props: {
    data: TypeSummary;
    transactionsParams: string;
}) {
    return (
        <>
            {props.data.accounts.map((account) => (
                <FullAccountTableAccountRow
                    key={account.account.id}
                    name={account.account.name}
                    amounts={account.amounts}
                    href={`/accounts/${account.account.id}/transactions?${props.transactionsParams}`}
                />
            ))}
            <FullAccountTableTotalRow
                name={props.data.typeName + " 합계"}
                amounts={props.data.sum}
                className="sub-total"
            />
        </>
    );
}

function ShortTypeSummaryRows(props: {
    data: TypeSummary;
    transactionsParams: string;
}) {
    return (
        <>
            {props.data.accounts.map((account) => (
                <ExternalAccountTableRow
                    key={account.account.id}
                    name={account.account.name}
                    amount={account.amounts.outAmount - account.amounts.inAmount}
                    href={`/accounts/${account.account.id}/transactions?${props.transactionsParams}`}
                />
            ))}
            <ExternalAccountTableRow
                name={props.data.typeName + " 합계"}
                amount={props.data.sum.outAmount - props.data.sum.inAmount}
                className="sub-total"
            />
        </>
    );
}